/**
 * Algo Performance Dashboard (2026-09-19) — a Backtest/Live toggle, 5 KPI
 * cards, and an interactive equity curve, embedded as a "Performance" tab
 * in `AlgoManagementPage.tsx`.
 *
 * Reads ONLY `algoPerformanceService` (a thin wrapper over
 * `GET /algo/{algoId}/performance/backtest` and `.../live`) — never
 * triggers a backtest, never aggregates trade history itself. Both calls
 * are cheap indexed reads of an already-persisted snapshot (see
 * `app.api.v1.algo_performance`'s own docstring on the backend), so both
 * modes are fetched once on mount and the toggle just switches which
 * already-fetched result is shown — no re-fetch, no loading flicker, on
 * every click.
 *
 * Every metric is rendered from a nullable field and shown as "— Not
 * available" rather than a fabricated number whenever the backend
 * couldn't compute it (see `data_quality_status`/`data_quality_notes`,
 * surfaced as a small info banner) — most commonly `NOT_YET_COMPUTED`
 * (no snapshot has been written yet), `NO_CAPITAL_BASIS` (this algo's
 * `capital_required` is unset/zero), `ZERO_TRADES`, or
 * `NO_QUALIFYING_BACKTEST` (no completed ~1-year backtest of the active
 * config exists yet).
 *
 * Exported (unlike `BacktestTab`/`MonitorTab` in the same page) so it's
 * directly testable with just an `algoId` prop and a mocked service —
 * see `AlgoPerformanceTab.test.tsx`.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import algoPerformanceService from "@/services/algoPerformanceService";
import { SummaryMetricCard } from "@/pages/options-dashboard/SummaryMetricCard";
import type {
  AlgoPerformanceMode,
  AlgoPerformanceSchedulerStatusRead,
  AlgoPerformanceSnapshotRead,
} from "@/types";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltip);

function toNum(value?: string | null): number | null {
  if (value === undefined || value === null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function fmtInr(value?: string | null): string {
  const n = toNum(value);
  if (n === null) return "—";
  const sign = n < 0 ? "-" : "";
  return `${sign}₹${Math.abs(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtCompactInr(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 100000) return `${sign}₹${(abs / 100000).toFixed(1)}L`;
  if (abs >= 1000) return `${sign}₹${(abs / 1000).toFixed(1)}k`;
  return `${sign}₹${abs.toFixed(0)}`;
}

function fmtPct(value?: string | null, digits = 2): string {
  const n = toNum(value);
  if (n === null) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(digits)}%`;
}

function fmtRatio(value?: string | null): string {
  const n = toNum(value);
  if (n === null) return "—";
  return n.toFixed(2);
}

const DATA_QUALITY_LABELS: Record<string, string> = {
  NOT_YET_COMPUTED: "No performance snapshot has been computed yet for this mode.",
  ZERO_TRADES: "No closed trades in this period.",
  NO_CAPITAL_BASIS: "This algo has no configured capital — Configured Capital, Annualized Return %, Calmar Ratio, and Max Drawdown % are unavailable.",
  NO_QUALIFYING_BACKTEST: "No completed backtest spanning roughly one year, run against the currently active config, has been found yet.",
};

function fmtDateTime(value?: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function useSchedulerStatus(algoId: string, refreshKey: number) {
  const [status, setStatus] = useState<AlgoPerformanceSchedulerStatusRead | null>(null);

  useEffect(() => {
    let cancelled = false;
    algoPerformanceService
      .getSchedulerStatus(algoId)
      .then((result) => {
        if (!cancelled) setStatus(result);
      })
      .catch(() => {
        if (!cancelled) setStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [algoId, refreshKey]);

  return status;
}

function useSnapshot(algoId: string, mode: AlgoPerformanceMode, refreshKey: number) {
  const [snapshot, setSnapshot] = useState<AlgoPerformanceSnapshotRead | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    const call =
      mode === "BACKTEST"
        ? algoPerformanceService.getBacktestPerformance(algoId)
        : algoPerformanceService.getLivePerformance(algoId);
    call
      .then((result) => {
        if (!cancelled) setSnapshot(result);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [algoId, mode, refreshKey]);

  return { snapshot, loading, error };
}

export function AlgoPerformanceTab({ algoId }: { algoId: string }) {
  const theme = useTheme();
  const [mode, setMode] = useState<AlgoPerformanceMode>("BACKTEST");
  const [refreshKey, setRefreshKey] = useState(0);
  const [recomputing, setRecomputing] = useState(false);
  const [recomputeError, setRecomputeError] = useState(false);
  const backtest = useSnapshot(algoId, "BACKTEST", refreshKey);
  const live = useSnapshot(algoId, "LIVE", refreshKey);
  const schedulerStatus = useSchedulerStatus(algoId, refreshKey);
  const active = mode === "BACKTEST" ? backtest : live;
  const snapshot = active.snapshot;

  const handleRecomputeNow = async () => {
    setRecomputing(true);
    setRecomputeError(false);
    try {
      await algoPerformanceService.recomputeLivePerformance(algoId);
      setRefreshKey((k) => k + 1);
    } catch {
      setRecomputeError(true);
    } finally {
      setRecomputing(false);
    }
  };

  const profitColor = theme.palette.success.main;
  const lossColor = theme.palette.error.main;

  const chartData = useMemo(() => {
    const points = snapshot?.equity_curve ?? [];
    return {
      labels: points.map((p) => p.point_date),
      datasets: [
        {
          label: "Cumulative P&L",
          data: points.map((p) => toNum(p.cumulative_pnl) ?? 0),
          borderColor: theme.palette.primary.main,
          backgroundColor: theme.palette.primary.main,
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.1,
        },
      ],
    };
  }, [snapshot, theme.palette.primary.main]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "nearest" as const, intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items: Array<{ dataIndex: number }>) => {
              const idx = items[0]?.dataIndex;
              const point = idx !== undefined ? snapshot?.equity_curve[idx] : undefined;
              return point ? point.point_date : "";
            },
            label: (item: { dataIndex: number }) => {
              const point = snapshot?.equity_curve[item.dataIndex];
              if (!point) return "";
              const dailyPnl = toNum(point.daily_pnl) ?? 0;
              const cumulativePnl = toNum(point.cumulative_pnl) ?? 0;
              const cumulativeValue = toNum(point.cumulative_value);
              const drawdownPct = toNum(point.drawdown_pct);
              const lines = [
                `Daily P&L: ${dailyPnl >= 0 ? "+" : ""}${fmtCompactInr(dailyPnl)}`,
                `Cumulative P&L: ${cumulativePnl >= 0 ? "+" : ""}${fmtCompactInr(cumulativePnl)}`,
              ];
              if (cumulativeValue !== null) lines.push(`Portfolio value: ${fmtCompactInr(cumulativeValue)}`);
              if (drawdownPct !== null) lines.push(`Drawdown: ${drawdownPct.toFixed(2)}%`);
              return lines;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: theme.palette.text.secondary, maxRotation: 0, autoSkip: true },
        },
        y: {
          grid: { color: theme.palette.divider },
          ticks: {
            color: theme.palette.text.secondary,
            callback: (v: number | string) => fmtCompactInr(Number(v)),
          },
        },
      },
    }),
    [snapshot, theme.palette.text.secondary, theme.palette.divider]
  );

  const points = snapshot?.equity_curve ?? [];
  const hasCurve = points.length > 0;
  const dataQualityNote =
    snapshot && snapshot.data_quality_status !== "OK"
      ? snapshot.data_quality_notes || DATA_QUALITY_LABELS[snapshot.data_quality_status]
      : null;

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} justifyContent="space-between">
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {mode === "BACKTEST" ? "Backtested Performance — Past 1 Year" : "Live Trading Performance"}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {mode === "BACKTEST"
              ? "Based on the most recent completed backtest of the active production config, spanning roughly one trailing year."
              : "Realized P&L from actual executed LIVE trades only — excludes paper/simulated positions and any unrealized P&L on still-open positions."}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {snapshot?.snapshot_date ? `As of ${snapshot.snapshot_date}` : "Not yet calculated"}
            {mode === "LIVE" && schedulerStatus && (
              <>
                {" · "}
                EOD scheduler: {schedulerStatus.enabled ? "enabled" : "disabled"}
                {schedulerStatus.enabled &&
                  ` (last success ${fmtDateTime(schedulerStatus.last_live_calculated_at)})`}
              </>
            )}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {mode === "LIVE" && (
            <Button
              size="small"
              variant="outlined"
              onClick={handleRecomputeNow}
              disabled={recomputing}
              startIcon={recomputing ? <CircularProgress size={14} /> : undefined}
            >
              {recomputing ? "Recomputing…" : "Recompute Now"}
            </Button>
          )}
          <ToggleButtonGroup
            size="small"
            value={mode}
            exclusive
            onChange={(_e, value) => {
              if (value) setMode(value as AlgoPerformanceMode);
            }}
          >
            <ToggleButton value="BACKTEST">Backtest</ToggleButton>
            <ToggleButton value="LIVE">Live</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      {active.error && <Alert severity="error">Algo performance is temporarily unavailable.</Alert>}
      {recomputeError && (
        <Alert severity="error" onClose={() => setRecomputeError(false)}>
          Recompute failed — please try again in a moment.
        </Alert>
      )}
      {!active.error && dataQualityNote && <Alert severity="info">{dataQualityNote}</Alert>}
      {mode === "LIVE" && schedulerStatus && !schedulerStatus.enabled && (
        <Alert severity="warning">
          The EOD live-performance scheduler is disabled — Live Performance will not refresh automatically until an
          admin enables it.
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(5, 1fr)" },
          gap: 2,
        }}
      >
        <SummaryMetricCard label="Configured Capital" value={fmtInr(snapshot?.configured_capital)} valueVariant="h5" />
        <SummaryMetricCard
          label="Annualized Return %"
          value={fmtPct(snapshot?.annualized_return_pct)}
          valueVariant="h5"
          accentColor={
            toNum(snapshot?.annualized_return_pct) === null
              ? undefined
              : (toNum(snapshot?.annualized_return_pct) ?? 0) >= 0
                ? profitColor
                : lossColor
          }
        />
        <SummaryMetricCard label="Calmar Ratio" value={fmtRatio(snapshot?.calmar_ratio)} valueVariant="h5" />
        <SummaryMetricCard
          label="Average Monthly Payout"
          value={fmtInr(snapshot?.avg_monthly_payout)}
          valueVariant="h5"
        />
        <SummaryMetricCard
          label="Maximum Drawdown %"
          value={fmtPct(snapshot?.max_drawdown_pct)}
          valueVariant="h5"
          accentColor={toNum(snapshot?.max_drawdown_pct) === null ? undefined : lossColor}
        />
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} mb={1}>
          Equity Curve
        </Typography>
        {active.loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        ) : hasCurve ? (
          <Box sx={{ height: { xs: 240, sm: 280 } }}>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <Line data={chartData as any} options={chartOptions as any} />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No equity curve data available for this mode yet.
          </Typography>
        )}
      </Paper>
    </Stack>
  );
}

export default AlgoPerformanceTab;
